import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { cx } from "shared/utils";
import config from "config/config";
import type { PaywallCopy } from "./constants";

interface BoomCrmPaywallProps extends PaywallCopy {
  className?: string;
  /**
   * "default" is the 666px card the full-page paywalls and hover popovers use
   * (Figma BoomCRM-Paywall 34:24027). "compact" is the 431px card anchored to a
   * locked composer tab (Figma 31:23290 email, 394:7658 call): shorter 190px
   * preview, 16px title, same copy and button. It renders `CompactPreview`
   * when the copy provides one, else the regular preview.
   */
  variant?: "default" | "compact";
}

const openDemo = () => {
  window.open(config.boomCrmDemoUrl, "_blank", "noopener,noreferrer");
};

export const BoomCrmPaywall = ({
  title,
  description,
  Preview,
  CompactPreview,
  className,
  variant = "default"
}: BoomCrmPaywallProps) => {
  const compact = variant === "compact";
  const PreviewComponent = compact ? (CompactPreview ?? Preview) : Preview;

  return (
    <div
      className={cx(
        "flex flex-col items-end gap-4 rounded-lg border border-alpha-100 bg-white p-4 shadow-lg",
        compact ? "w-[431px] max-w-[calc(100vw-2rem)]" : "w-full max-w-[666px]",
        className
      )}
    >
      <PreviewComponent />
      <div className="flex w-full flex-col items-start gap-2">
        <h2
          className={cx(
            "font-bold text-content-1000",
            compact ? "text-base leading-6" : "text-xl leading-8"
          )}
        >
          {title}
        </h2>
        <p className="text-sm font-medium leading-5 text-content-400">
          {description}
        </p>
      </div>
      <Button
        variant={ButtonVariant.PRIMARY}
        size={ButtonSize.SM}
        onClick={openDemo}
      >
        Book a demo
      </Button>
    </div>
  );
};
